import { useTemplatesStore } from '@/stores/templates'

import {
  repositionAndRescaleElement,
  repositionAndRescaleImageElement,
  adjustH1FontSize,
} from '@/helpers/coverVariantResize'
import { getActualPageSize } from '@/helpers/coverVariant'
import { kindleCoverDimensions } from '@/constants/coverDimensions'

import {
  formatDecimalNumber,
  getAveragePadding,
  getNumberFontWeight,
  getTextContentWithNewLines,
  isBackgroundTransparent,
  normalizeBorderRadius,
} from '@/helpers/styles'

import type {
  TAmazonCoverGroup,
  TAmazonCoverSection,
  TAmazonCoverThumbnailInfo,
  TAmazonElement,
  TAmazonElementBackground,
  TAmazonElementImage,
  TAmazonElementText,
  TAmazonPageSetup,
  TAmazonTextElementType,
  TAmazonCssSelectors,
  TAmazonBindingType,
} from '@/types/amazon-cover'

import type { TTemplate, TTemplateVariant } from '@/types/templates'
import type { TProject } from '@/types/projects'
import { v4 } from 'uuid'

const subtitleSelector = ':is(h2,h3,h4,h5,p):not(.author-name):not(#authorwrap):not(#authorwrap *):not(.author-name *)'

export const cssSelectors: TAmazonCssSelectors = {
  title: 'h1',
  subtitle: subtitleSelector,
  secondSubtitle: subtitleSelector,
  author: ':is(.author-name, #authorwrap)',
  summary: 'p',
}

export const pixelsToMMRatio = 0.265

// Kindle size is 1123 to 702 but in mm. Amazon requires proportion 1.6:1
export const kindleSize = {
  width: kindleCoverDimensions.width,
  height: kindleCoverDimensions.height,
}

export const minimumPageCountForUsingTextOnSpine = 80

function pixelsToMM(pixels?: number | string): number {
  if (pixels === undefined || pixels === null) {
    return 0
  }

  const num = typeof pixels === 'number' ? pixels : parseFloat(pixels)
  if (isNaN(num)) {
    return 0
  }

  return formatDecimalNumber(num * pixelsToMMRatio)
}

function prepareFontFamily(fontFamily: string | undefined): string {
  if (!fontFamily) {
    return '"Roboto"'
  }

  //remove existing quotes if they are here and add double quotes
  const trimmed = fontFamily.replace(/^['"]|['"]$/g, '')
  return `"${trimmed}"`
}

function skipPotentiallyWrongSubtitleCandidates(
  page: HTMLElement,
  textElements: NodeListOf<HTMLElement>,
): HTMLElement[] {
  const pageRect = page.getBoundingClientRect()
  if (!pageRect?.height || !pageRect?.width) {
    return []
  }

  const pageHeight = pageRect.height
  const pageWidth = pageRect.width

  return [...textElements].filter((el) => {
    const elRect = el.getBoundingClientRect()
    if (!elRect?.height || !elRect?.width) {
      return false
    }

    //validate the text first. dont't use default, unmodified text elements
    const text = el.textContent?.trim() ?? ''
    const isExactHeading = text === 'Heading'
    const isEmpty = text.length === 0
    const isParagraphPlaceholder = el.tagName === 'P' && text.includes('This is a text placeholder')
    if (isExactHeading || isEmpty || isParagraphPlaceholder) {
      return false
    }

    //use top and left used in absolute elements inline styles (should be always present, if not, don't use element)
    const topStr = (el as HTMLElement).style.top
    const leftStr = (el as HTMLElement).style.left
    if (topStr === '' || leftStr === '') {
      return false
    }
    const elTop = parseFloat(topStr)
    const elLeft = parseFloat(leftStr)
    if (Number.isNaN(elTop) || Number.isNaN(elLeft)) {
      return false
    }

    //finally, check if element is not outside the page bounds
    //some broken elements like this exist in some templates
    //if half of  the element is outside the page, we consider it out of bounds
    const isAbovePage = elTop < 0 && Math.abs(elTop) > elRect.height / 2
    const isLeftOfPage = elLeft < 0 && Math.abs(elLeft) > elRect.width / 2
    const isRightOfPage = elLeft > pageWidth - elRect.width / 2
    const isUnderPage = elTop > pageHeight - elRect.height / 2

    return !isAbovePage && !isLeftOfPage && !isRightOfPage && !isUnderPage
  })
}

function getPageTextContainerInfo(
  doc: Document,
  page: HTMLElement,
  label: TAmazonCoverGroup,
  type: TAmazonTextElementType,
  selector: string,
): TAmazonElementText | null {
  let textElement: HTMLElement | null = null

  if (type !== 'second-subtitle' && type !== 'subtitle') {
    textElement = page.querySelector(selector) as HTMLElement | null
  } else {
    const textElements = skipPotentiallyWrongSubtitleCandidates(page, page.querySelectorAll(selector))
    if (type === 'subtitle' && textElements.length > 0) {
      textElement = textElements[0]!
    } else if (type === 'second-subtitle' && textElements.length > 1) {
      textElement = textElements[1]!
    }
  }

  if (!textElement || !doc.defaultView) {
    return null
  }
  if (textElement.id === 'authorwrap' || textElement.classList.contains('author-name')) {
    const innerTextElement = textElement.querySelector(':is(h2,h3,h4,h5,p)')
    if (innerTextElement) {
      textElement = innerTextElement as HTMLElement
    }
  }
  const parent = textElement.closest('.cover-page-container, .back-page-container')
  if (!parent) {
    return null
  }

  const parentRect = parent.getBoundingClientRect()
  let rect = null
  let styles = null

  if (textElement) {
    rect = textElement.getBoundingClientRect()
    styles = doc.defaultView.getComputedStyle(textElement)
  }

  const defaultFontSize = 20
  const fontSize = styles && styles.fontSize !== '0px' ? pixelsToMM(parseInt(styles.fontSize)) : defaultFontSize
  const defaultLineHeight = 1.2
  const lineHeight =
    styles && styles.lineHeight !== 'normal'
      ? formatDecimalNumber(pixelsToMM(parseFloat(styles.lineHeight)) / fontSize) || defaultLineHeight
      : defaultLineHeight

  return {
    id: `${label}-${type}`,
    top: rect && parentRect ? pixelsToMM(rect.top - parentRect.top) : 0,
    left: rect && parentRect ? pixelsToMM(rect.left - parentRect.left) : 0,
    zIndex: styles ? parseInt(styles.zIndex) : 1,
    type: type,
    enabled: true,
    textAlign: styles ? styles.textAlign : 'center',
    fontFamily: prepareFontFamily(styles?.fontFamily),
    fontWeight: styles ? getNumberFontWeight(styles.fontWeight) : 400,
    textContent: getTextContentWithNewLines(textElement),
    fontSize,
    lineHeight,
    fontStyle: styles && styles.fontStyle === 'italic' ? 'italic' : 'normal',
    color: styles ? styles.color : '#000',
    backgroundColor: styles ? styles.backgroundColor : 'transparent',
    padding: pixelsToMM(formatDecimalNumber(getAveragePadding(textElement, styles?.padding ?? '0px'))),
    width: rect ? pixelsToMM(formatDecimalNumber(rect.width)) : 0,
    group: label,
  }
}

function getBackgroundInfo(
  doc: Document,
  element: HTMLElement | null,
  label: TAmazonCoverGroup,
): TAmazonElementBackground | null {
  if (!element || !doc.defaultView) {
    return null
  }
  const docWindow = doc.defaultView
  const styles = docWindow.getComputedStyle(element)
  const rect = element.getBoundingClientRect()

  return {
    id: `${label}-background`,
    top: styles.top ? pixelsToMM(formatDecimalNumber(styles.top)) : 0,
    left: styles.top ? pixelsToMM(formatDecimalNumber(styles.left)) : 0,
    zIndex: Number.isNaN(parseInt(styles.zIndex)) ? 0 : parseInt(styles.zIndex),
    type: 'background',
    enabled: true,
    backgroundColor: styles.backgroundColor ? styles.backgroundColor : 'transparent',
    width: pixelsToMM(formatDecimalNumber(rect.width)),
    height: pixelsToMM(formatDecimalNumber(rect.height)),
    group: label,
  }
}

function getBackgroundComparedInfo(
  doc: Document,
  page: HTMLElement | null,
  label: TAmazonCoverGroup,
): TAmazonElementBackground | null {
  const pageBackground = getBackgroundInfo(doc, page, label)
  //old template system had #cover-page-content and #back-page-content divs
  const contentBackground = page ? getBackgroundInfo(doc, page.querySelector(`#${label}-page-content`), label) : null

  if (!pageBackground) {
    return null
  } else if (!contentBackground) {
    return pageBackground
  }

  const isContentTransparent = isBackgroundTransparent(contentBackground.backgroundColor ?? '')

  return {
    id: pageBackground?.id ?? 'front-background',
    top: isContentTransparent ? pageBackground.top : contentBackground.top,
    left: isContentTransparent ? pageBackground.left : contentBackground.left,
    zIndex: isContentTransparent ? pageBackground.zIndex : contentBackground.zIndex,
    type: 'background',
    enabled: isContentTransparent ? pageBackground?.enabled : contentBackground.enabled,
    backgroundColor: isContentTransparent ? pageBackground.backgroundColor : contentBackground.backgroundColor,
    width: isContentTransparent ? pageBackground.width : contentBackground.width,
    height: isContentTransparent ? pageBackground.height : contentBackground.height,
    group: pageBackground.group,
  }
}

function getPagePlaceholdersInfo(
  doc: Document,
  dimensions: TAmazonPageSetup,
  bindingType: TAmazonBindingType,
): TAmazonElementImage[] {
  const coverContainerToUse = doc.querySelector('.cover-page-container')
  const backContainerToUse = doc.querySelector('.back-page-container')

  const emptyNodeList = document.createDocumentFragment().querySelectorAll<HTMLImageElement>('img')

  const coverPlaceholders = coverContainerToUse
    ? (coverContainerToUse.querySelectorAll<HTMLImageElement>(
        'img.absolute-image-placeholder',
      ) as NodeListOf<HTMLImageElement>)
    : emptyNodeList

  const backPlaceholders = backContainerToUse
    ? (backContainerToUse.querySelectorAll<HTMLImageElement>(
        'img.absolute-image-placeholder',
      ) as NodeListOf<HTMLImageElement>)
    : emptyNodeList

  const imagesPlaceholders: HTMLImageElement[] = [...coverPlaceholders, ...backPlaceholders]

  const placeholdersInfo: TAmazonElementImage[] = []
  const docWindow = doc.defaultView
  if (!docWindow) {
    return placeholdersInfo
  }

  imagesPlaceholders.forEach((imgPlaceholder: HTMLImageElement) => {
    const styles = docWindow.getComputedStyle(imgPlaceholder)
    let leftOffset = 0
    let topOffset = 0

    const isOnCover = !!imgPlaceholder.closest('.cover-page-container')

    if (bindingType !== 'kindle') {
      // Can be on cover or on back, calculate left differently
      leftOffset = !isOnCover ? dimensions.back.x : dimensions.back.x + dimensions.back.width + dimensions.spine.width
      topOffset = dimensions.back.y
    }

    if (bindingType === 'kindle' && !isOnCover) {
      return
    }

    placeholdersInfo.push({
      id: v4(),
      borderRadius: styles?.borderRadius ? normalizeBorderRadius(styles?.borderRadius) : '0px',
      type: 'custom-image',
      group: 'none',
      enabled: true,
      zIndex: 0,
      left: formatDecimalNumber(pixelsToMM(parseFloat(styles.left)) + leftOffset),
      top: formatDecimalNumber(pixelsToMM(parseFloat(styles.top)) + topOffset),
      width: formatDecimalNumber(pixelsToMM(parseFloat(styles.width))),
      src: imgPlaceholder.src,
    })
  })

  return placeholdersInfo
}

function getPageImageInfo(
  label: TAmazonCoverGroup,
  coverThumbnailsInfo: TAmazonCoverThumbnailInfo | null,
): TAmazonElementImage {
  let imageSrc: string = ''
  let imageWidth = 0
  let imageHeight = 0
  if (label === 'front' && coverThumbnailsInfo?.cover_thumbnail) {
    imageSrc = coverThumbnailsInfo.cover_thumbnail
    imageWidth = pixelsToMM(coverThumbnailsInfo?.cover_info?.width) ?? 0
    imageHeight = pixelsToMM(coverThumbnailsInfo?.cover_info?.height) ?? 0
  } else if (label === 'back' && coverThumbnailsInfo?.back_thumbnail) {
    imageSrc = coverThumbnailsInfo.back_thumbnail
    imageWidth = pixelsToMM(coverThumbnailsInfo?.back_info?.width) ?? 0
    imageHeight = pixelsToMM(coverThumbnailsInfo?.back_info?.height) ?? 0
  }

  return {
    id: `${label}-image`,
    top: 0,
    left: 0,
    zIndex: 0,
    type: 'image',
    width: imageWidth,
    height: imageHeight,
    src: imageSrc,
    enabled: true,
    group: label,
  }
}

export async function getSpineData(
  dimensions: TAmazonPageSetup,
  pagesCount: number,
  front: TAmazonElement[],
  dominantColors: string[],
): Promise<TAmazonElement[]> {
  const spineBackgroundData = {
    ...front.find((e: TAmazonElement) => e.type === 'background' || e.id === 'front-background'),
    ...{ id: 'spine-background', group: 'spine' },
  } as TAmazonElementBackground

  spineBackgroundData.backgroundColor = dominantColors.length
    ? dominantColors[0]
    : spineBackgroundData.backgroundColor || '#FFFFFF'
  spineBackgroundData.zIndex = 1
  spineBackgroundData.enabled = true
  spineBackgroundData.left = (dimensions.spine.x || dimensions.spine.x_no_margin) ?? 0
  spineBackgroundData.top =
    (dimensions.spine.y - dimensions.margin.height || (dimensions.spine.y_no_margin ?? 0) - dimensions.margin.height) ??
    0
  spineBackgroundData.width = (dimensions.spine.width || dimensions.spine.width_no_margin) ?? 0
  spineBackgroundData.height =
    (dimensions.spine.height + 2 * dimensions.margin.height ||
      (dimensions.spine.height_no_margin ?? 0) + 2 * dimensions.margin.height) ??
    0

  if (pagesCount < minimumPageCountForUsingTextOnSpine) {
    return [spineBackgroundData]
  }

  const frontTitleData = front.find((e: TAmazonElement) => e.id === 'front-title') as TAmazonElementText
  const frontAuthorData = front.find((e: TAmazonElement) => e.id === 'front-author') as TAmazonElementText
  const spineTitleYNoMargin = dimensions.spine.y_no_margin || dimensions.spine.y

  const spineTitleData: TAmazonElementText = {
    id: 'spine-title',
    top: formatDecimalNumber(spineTitleYNoMargin + (spineTitleYNoMargin - dimensions.spine.y)),
    left: formatDecimalNumber(dimensions.spine.x_no_margin || dimensions.spine.x),
    zIndex: 2,
    type: 'title',
    enabled: true,
    lineHeight: 1.2,
    textAlign: frontTitleData?.textAlign ?? 'left',
    fontFamily: prepareFontFamily(frontTitleData?.fontFamily),
    fontWeight: frontTitleData?.fontWeight ?? 400,
    textContent: frontTitleData?.textContent ?? 'Book Title',
    fontSize: Math.round(10 * (dimensions.spine.width_no_margin || dimensions.spine.width) * 0.8) / 10,
    color: frontTitleData?.color ?? '#000',
    backgroundColor: 'transparent',
    padding: 0,
    group: 'spine',
  }

  const spineAuthorData: TAmazonElementText = {
    id: 'spine-author',
    top: (dimensions.spine.height || dimensions.spine.height_no_margin) ?? 0,
    left: dimensions.spine.x_no_margin || dimensions.spine.x,
    zIndex: 2,
    type: 'author',
    enabled: true,
    lineHeight: 1.2,
    textAlign: frontAuthorData?.textAlign ?? 'left',
    fontFamily: prepareFontFamily(frontAuthorData?.fontFamily),
    fontWeight: frontAuthorData?.fontWeight ?? 400,
    textContent: frontAuthorData?.textContent ?? 'Author Name',
    fontSize: Math.round(10 * (dimensions.spine.width_no_margin || dimensions.spine.width) * 0.8) / 10,
    color: frontAuthorData?.color ?? '#000',
    backgroundColor: 'transparent',
    padding: 0,
    group: 'spine',
  }

  const safetyHeightOffset = 0.5

  //max 35% of spine height for author text. looks good in most cases, we may adjust.
  const maxAuthorHeight = Math.round(spineBackgroundData.height * (35 / 100))
  const authorAdjustment = await adjustTargetHeight(spineAuthorData, maxAuthorHeight)
  const newAuthorHeight = authorAdjustment.height

  if (authorAdjustment.trimCount > 0) {
    spineAuthorData.textContent = spineAuthorData.textContent.slice(0, -authorAdjustment.trimCount)
  }

  spineAuthorData.fontSize = authorAdjustment.fontSize
  spineAuthorData.height = newAuthorHeight + safetyHeightOffset
  spineAuthorData.top = spineAuthorData.top - newAuthorHeight - spineTitleYNoMargin + 1
  spineAuthorData.left =
    spineAuthorData.left + ((dimensions.spine.width_no_margin || 0) / 2 - authorAdjustment.width / 2)

  //max 53% of spine height for title text. looks good in most cases, we may adjust.
  const maxTitleHeight = Math.round(spineBackgroundData.height * (53 / 100))
  const titleAdjustment = await adjustTargetHeight(spineTitleData, maxTitleHeight)
  const newTitleHeight = titleAdjustment.height

  if (titleAdjustment.trimCount > 0) {
    spineTitleData.textContent = spineTitleData.textContent.slice(0, -titleAdjustment.trimCount)
  }

  spineTitleData.fontSize = titleAdjustment.fontSize
  spineTitleData.height = newTitleHeight + safetyHeightOffset
  spineTitleData.left = spineTitleData.left + ((dimensions.spine.width_no_margin || 0) / 2 - titleAdjustment.width / 2)

  return [spineBackgroundData, spineTitleData, spineAuthorData]
}

export function actualizeCustomFontLinks(usedFonts: { customFontLinks: string[] }, docFromParam?: Document) {
  const docToUse = docFromParam ?? document
  docToUse.querySelectorAll('link[data-font="cover"]').forEach((el) => el.remove())
  usedFonts.customFontLinks.forEach((href) => {
    if (!href?.length) return

    const link = docToUse.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.dataset.font = 'cover'

    docToUse.head.appendChild(link)
  })
}

function getBarcodeInfo(
  doc: Document,
  dimensions: TAmazonPageSetup,
  page: HTMLElement,
  label: TAmazonCoverGroup,
): TAmazonElementImage | null {
  const barcodeWidthMM = 50.8 //from Amazon KDP doc
  const barcodeHeightMM = 30.5 //from Amazon KDP doc
  const barcodeBottomMarginMM = 19 //minimum margin value from Amazon KDP doc
  const barcodeRightMarginMM = 6 //minimum margin value from Amazon KDP doc

  const pageLeftAndRightMarginSum =
    dimensions.back.width && dimensions.back.width_no_margin
      ? dimensions.back.width - dimensions.back.width_no_margin
      : 0
  const leftPageMargin =
    dimensions.back?.x_no_margin && dimensions.back?.x ? dimensions.back.x_no_margin - dimensions.back.x : 0
  const rightPageMargin = pageLeftAndRightMarginSum - leftPageMargin
  const bottomPageMargin =
    dimensions.back?.y_no_margin && dimensions.back?.y ? dimensions.back.y_no_margin - dimensions.back.y : 0

  return {
    id: `${label}-barcode`,
    enabled: true,
    group: label,
    type: 'barcode',
    top: formatDecimalNumber(dimensions.back.height - barcodeHeightMM - bottomPageMargin - barcodeBottomMarginMM),
    left: formatDecimalNumber(dimensions.back.width - barcodeWidthMM - rightPageMargin - barcodeRightMarginMM),
    zIndex: 2,
    width: barcodeWidthMM,
    height: barcodeHeightMM,
    src: 'https://designrr.s3.amazonaws.com/images/sample_barcode.svg',
  }
}

export function getInteriorSizeInMM(iframe: HTMLIFrameElement): { width: number; height: number } | null {
  if (!iframe || !iframe.contentDocument) {
    return null
  }

  const doc = iframe.contentDocument
  const page = doc.querySelector('.page-container') as HTMLElement | null

  if (!page) {
    return null
  }

  const widthPx = page.offsetWidth
  const heightPx = page.offsetHeight

  const width = formatDecimalNumber(widthPx * pixelsToMMRatio)
  const height = formatDecimalNumber(heightPx * pixelsToMMRatio)

  return { width, height }
}

function getPageInfo(
  doc: Document,
  dimensions: TAmazonPageSetup,
  label: TAmazonCoverGroup,
  coverThumbnailsInfo: TAmazonCoverThumbnailInfo | null,
  [shiftX, shiftY]: [number, number] = [0, 0],
): TAmazonElement[] | null {
  const selector: string = label === 'front' ? '.cover-page-container' : '.back-page-container'
  const page: HTMLElement | null = doc.querySelector(selector) as HTMLElement | null

  if (!page) {
    return null
  }

  return [
    getBackgroundComparedInfo(doc, page, label),
    getPageImageInfo(label, coverThumbnailsInfo),
    getPageTextContainerInfo(doc, page, label, 'title', cssSelectors.title),
    label === 'front' && getPageTextContainerInfo(doc, page, label, 'subtitle', cssSelectors.subtitle),
    label === 'front' && getPageTextContainerInfo(doc, page, label, 'second-subtitle', cssSelectors.secondSubtitle),
    getPageTextContainerInfo(doc, page, label, 'author', cssSelectors.author),
    label === 'back' && getPageTextContainerInfo(doc, page, label, 'summary', cssSelectors.summary),
    label === 'back' && getBarcodeInfo(doc, dimensions, page, label),
  ]
    .filter((item): item is Exclude<typeof item, false | null> => Boolean(item))
    .map((item) => {
      item.left += shiftX
      item.top += shiftY
      return item
    })
}

function setBackgroundToFullWidth(
  elements: TAmazonElement[],
  dimensions: TAmazonPageSetup,
  bindingType: TAmazonBindingType,
): TAmazonElement[] {
  let spineAuthorTopAdjustment = 0
  elements.forEach((element) => {
    if (
      bindingType !== 'kindle' &&
      ['back-image', 'front-image', 'back-background', 'front-background', 'spine-background'].includes(element.id) &&
      element.group in dimensions
    ) {
      const group: TAmazonCoverSection = dimensions[element.group as 'spine' | 'front' | 'back']
      if (element.id === 'spine-background' && group.height_background) {
        spineAuthorTopAdjustment = group.height_background - (element.height ?? 0)
      }

      element.left = group.x_background ?? element.left
      element.top = group.y_background ?? element.top
      element.width = group.width_background ?? element.width
      element.height = group.height_background ?? element.height
    }
  })

  elements.forEach((element) => {
    if (['spine-author'].includes(element.id) && element.group in dimensions) {
      element.top = formatDecimalNumber(element.top + spineAuthorTopAdjustment)
    }

    if (bindingType === 'kindle' && element?.id?.startsWith('front')) {
      element.left = formatDecimalNumber(
        element.left - dimensions.spine.width - dimensions.back.width - dimensions.back.x,
      )

      element.top = formatDecimalNumber(element.top - dimensions.front.y)
    }
  })

  return elements
}

function repositionAndRescaleAbsoluteElementsInPage(
  page: HTMLElement,
  originalWidth: number,
  originalHeight: number,
  variant?: TTemplateVariant,
) {
  if (!page?.children || !variant?.id || !originalHeight || !originalWidth) {
    return
  }
  const absoluteElements = Array.from(page.children) as HTMLElement[]
  const actualPageSize = getActualPageSize(page)
  const actualPageWidth = actualPageSize?.width ?? 0
  const actualPageHeight = actualPageSize?.height ?? 0

  absoluteElements.forEach((element: HTMLElement) => {
    if (element.tagName === 'IMG') {
      repositionAndRescaleImageElement(page, element, originalHeight, originalWidth, actualPageHeight, actualPageWidth)
    } else {
      repositionAndRescaleElement(page, element, originalHeight, originalWidth, actualPageHeight, actualPageWidth)
    }
  })

  adjustH1FontSize(page, variant, { width: actualPageWidth, height: actualPageHeight })
}

function updateTextElementsOnCoverFromVariant(cover?: HTMLElement, project?: TProject) {
  if (!cover || !project) {
    return
  }

  const newCoverAuthor =
    project?.author_first_name?.length && project?.author_last_name?.length
      ? project.author_first_name + ' ' + project.author_last_name
      : project.author_email
  const newCoverTitle = project?.name?.length ? project.name : ''
  const h1ToUpdate = cover.querySelector('h1') as HTMLHeadingElement | null
  const authorToUpdate = cover.querySelector('#authorwrap, .author-name') as HTMLElement | null

  if (h1ToUpdate) {
    h1ToUpdate.textContent = newCoverTitle
  }

  if (authorToUpdate) {
    authorToUpdate.textContent = newCoverAuthor
  }
}

function addVariantCssToPageStyles(page?: HTMLElement, variant?: TTemplateVariant) {
  if (!page || !variant?.css) {
    return
  }

  const doc = page.ownerDocument
  if (!doc) {
    return
  }

  const styleTag: HTMLStyleElement = doc.createElement('style')
  styleTag.id = 'variantstyles'
  let cssSumToAdd = variant.css
  const innerVariants = variant?.inner_variants ?? []
  innerVariants.forEach((innerVariant) => {
    if (innerVariant?.inner_variant?.css) {
      cssSumToAdd += '\n' + innerVariant.inner_variant.css
    }
  })
  styleTag.textContent = cssSumToAdd

  const iframeHead = doc.head
  if (iframeHead) {
    iframeHead.appendChild(styleTag)
  }
}

export async function useTemplateCoverAndBackIfNeeded(iframe: HTMLIFrameElement, project?: TProject) {
  if (!iframe || !iframe?.contentDocument?.body || !project?.template_id) {
    return
  }

  const templateId = project.template_id
  const previewIframeBody = iframe.contentDocument.body
  const coverPresent = isPagePresent(iframe, 'cover')
  const backPresent = isPagePresent(iframe, 'back')

  if (coverPresent && backPresent) {
    return
  }

  const templates = useTemplatesStore()
  const template: TTemplate | null = await templates.getTemplateById(templateId)
  const variants = template?.variants ?? []

  const html = template?.pages?.[0]?.html
  const css = template?.pages?.[0]?.css
  if (!html?.length || !css?.length || !variants?.length) {
    return
  }

  const styleEl = iframe.contentDocument.createElement('style')
  styleEl.textContent = css
  if (iframe.contentDocument?.head) {
    iframe.contentDocument.head.appendChild(styleEl)
  }

  const templateWidth = template?.entire_page_width ?? 794
  const templateHeight = template?.entire_page_height ?? 1123

  const parser = new DOMParser()
  const templateDoc = parser.parseFromString(html, 'text/html')

  if (!coverPresent) {
    const cover = templateDoc.querySelector('div.cover-page-container') as HTMLElement
    if (cover) {
      updateTextElementsOnCoverFromVariant(cover, project)
      previewIframeBody.prepend(cover)
      const coverDefaultVariant: TTemplateVariant | undefined = variants.find(
        (el) => el.element_subtype === 'cover-page-container',
      )
      addVariantCssToPageStyles(cover, coverDefaultVariant)
      repositionAndRescaleAbsoluteElementsInPage(cover, templateWidth, templateHeight, coverDefaultVariant)
    }
  }

  if (!backPresent) {
    const back = templateDoc.querySelector('div.back-page-container') as HTMLElement
    if (back) {
      const inlineScriptDiv = previewIframeBody.querySelector('#inlineScript-loaded')
      if (inlineScriptDiv) {
        previewIframeBody.insertBefore(back, inlineScriptDiv)
      } else {
        previewIframeBody.appendChild(back)
      }
      const backDefaultVariant: TTemplateVariant | undefined = variants.find(
        (el) => el.element_subtype === 'back-page-container',
      )
      addVariantCssToPageStyles(back, backDefaultVariant)
      repositionAndRescaleAbsoluteElementsInPage(back, templateWidth, templateHeight, backDefaultVariant)
    }
  }
}

function isPagePresent(iframe: HTMLIFrameElement, type: string): boolean {
  if (iframe && iframe.contentDocument) {
    const page = iframe.contentDocument.querySelector(`.${type}-page-container`)
    return !!page
  }

  return false
}

function cleanFontForGoogle(font: string): string {
  return font
    .replace(/\([^)]*\)/g, '')
    .replace(/["']/g, '')
    .trim()
    .replace(/\s+/g, '+')
}

async function waitForFont(
  fontFamily: string,
  fontWeight: number = 400,
  timeoutMs = 100,
  maxAttempts = 60,
): Promise<boolean> {
  const fontSpec = `${fontWeight} 32px ${fontFamily}`
  const testText = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

  for (let i = 0; i < maxAttempts; i++) {
    await document.fonts.load(fontSpec, testText)

    if (document.fonts.check(fontSpec, testText)) {
      return true
    }

    await new Promise((r) => setTimeout(r, timeoutMs))
  }

  console.warn(`Font "${fontFamily}" weight ${fontWeight} not loaded after timeout`)
  return false
}

async function adjustTargetHeight(
  el: TAmazonElementText,
  maxHeight: number,
): Promise<{ height: number; width: number; fontSize: number; trimCount: number }> {
  const div = document.createElement('div')
  const link = document.createElement('link')

  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${cleanFontForGoogle(el.fontFamily)}:wght@100;200;300;400;500;600;700;800;900&display=swap`
  document.head.append(link)

  div.textContent = el.textContent
  div.style.fontFamily = el.fontFamily
  div.style.fontWeight = `${el.fontWeight || 400}`
  div.style.opacity = '0'
  div.style.lineHeight = '1.2'
  div.style.position = 'absolute'
  div.style.left = '-100000px'
  div.style.writingMode = 'vertical-rl'
  div.style.whiteSpace = 'nowrap'

  document.body.append(div)

  try {
    //check in the loop until font is loaded and rendered
    await waitForFont(el.fontFamily, el.fontWeight || 400)
    //still keep short timeout after requestAnimationFrame just in case
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 500)))

    let fontSize = el.fontSize
    div.style.fontSize = `${fontSize}px`

    let rect = div.getBoundingClientRect()

    const minimumFontSize = 4.6
    const decreaseStep = 0.1

    while (rect.height > maxHeight && fontSize > minimumFontSize) {
      if (fontSize - decreaseStep < minimumFontSize) {
        break
      }

      fontSize -= decreaseStep
      div.style.fontSize = `${fontSize}px`
      rect = div.getBoundingClientRect()
    }

    let trimCount = 0
    while (rect.height > maxHeight && div.textContent.length > 0) {
      div.textContent = div.textContent.slice(0, -1)
      rect = div.getBoundingClientRect()
      trimCount += 1
    }

    div.remove()
    link.remove()
    return {
      height: Math.ceil(rect.height), // height up, to prevent "edge" issues
      width: rect.width,
      fontSize: Math.floor(fontSize), //font down, to prevent "edge" issues
      trimCount,
    }
  } catch {
    div.remove()
    link.remove()

    return {
      height: el.height || 0,
      fontSize: el.fontSize,
      width: el.width || 0,
      trimCount: 0,
    }
  }
}

export function getPageVariantSelector(iframe: HTMLIFrameElement, mode: 'cover' | 'back'): string {
  if (!iframe || !iframe.contentDocument) {
    return ''
  }

  const page: HTMLElement | null = iframe.contentDocument.querySelector(`.${mode}-page-container`) as HTMLElement | null
  if (!page) {
    return ''
  }

  const variantClass = [...page.classList].find((cls: string) => cls.startsWith('_div_'))
  if (!variantClass) {
    return ''
  }

  return variantClass
}

export function setFullUserNameAndTitleOnPreview(
  iframe: HTMLIFrameElement,
  fullNameOrEmail: string,
  newCoverTitle: string,
): void {
  if (!fullNameOrEmail?.length || !iframe || !iframe.contentDocument) {
    return
  }

  const authorContainers = iframe.contentDocument?.querySelectorAll(
    '.cover-page-container #authorwrap, .cover-page-container .author-name, .back-page-container #authorwrap, .back-page-container .author-name',
  )

  authorContainers.forEach((element) => {
    const innerTextElement = element.querySelector(':is(h2,h3,h4,h5,p)')
    if (innerTextElement) {
      innerTextElement.textContent = fullNameOrEmail
    } else {
      element.textContent = fullNameOrEmail
    }
  })

  setTitleOnPreview(iframe, newCoverTitle)
}

export function setSummaryOnPreview(iframe: HTMLIFrameElement, summary: string): void {
  if (!summary?.length || !iframe || !iframe.contentDocument) {
    return
  }

  const summaryEl = iframe.contentDocument.querySelector(`.back-page-container ${cssSelectors.summary}`)
  if (summaryEl) {
    summaryEl.textContent = summary
  }
}

export function setTitleOnPreview(iframe: HTMLIFrameElement, newCoverTitle: string) {
  if (!newCoverTitle?.length || !iframe || !iframe.contentDocument) {
    return
  }

  const headers = iframe.contentDocument?.querySelectorAll('.cover-page-container h1')
  headers.forEach((header) => {
    header.textContent = newCoverTitle
  })
}

export async function getCoverAndBackCoverInfo(
  iframe: HTMLIFrameElement,
  coverThumbnailsInfo: TAmazonCoverThumbnailInfo | null,
  dimensions: TAmazonPageSetup,
  pagesCount: number,
  bindingType: TAmazonBindingType,
): Promise<TAmazonElement[]> {
  if (!iframe) {
    console.error('Project preview iframe not found')
    return []
  }

  if (iframe && iframe.contentDocument) {
    const doc = iframe.contentDocument
    const front =
      getPageInfo(doc, dimensions, 'front', coverThumbnailsInfo, [dimensions.front.x, dimensions.front.y]) ?? []
    const back = getPageInfo(doc, dimensions, 'back', coverThumbnailsInfo, [dimensions.back.x, dimensions.back.y]) ?? []
    const spine = await getSpineData(dimensions, pagesCount, front, coverThumbnailsInfo?.dominant_colors ?? [])
    const customImages: TAmazonElementImage[] = getPagePlaceholdersInfo(doc, dimensions, bindingType)

    let adjustedResults: TAmazonElement[] = setBackgroundToFullWidth(
      [...back, ...spine, ...front, ...customImages],
      dimensions,
      bindingType,
    )

    if (bindingType === 'kindle') {
      adjustedResults = adjustedResults.filter((item: TAmazonElement): boolean => {
        return item?.id?.startsWith('front') || item?.type === 'custom-image'
      })

      //adjust front image dims to match background dims in jsons
      //adjustment for increased background sizes
      const frontImage = adjustedResults.find((item: TAmazonElement) => item.id === 'front-image')
      const frontBackground = adjustedResults.find((item: TAmazonElement) => item.id === 'front-background')
      if (frontImage && frontBackground) {
        frontImage.width = frontBackground.width
        frontImage.height = frontBackground.height
      }
    }

    return adjustedResults
  } else {
    console.log('Project preview iframe contentDocument is inaccessible')
    return []
  }
}
